"use client";

import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { useTranslations } from "@/i18n/LocaleProvider";

interface SubmitContactMessageResponse {
  id: string;
}

// Deliberately unauthenticated -- unlike the booking flows, a general inquiry shouldn't require
// phone verification first (see functions/src/callable/submitContactMessage.ts).
export default function ContactForm() {
  const { t } = useTranslations();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setStatus("idle");
    try {
      const submitContactMessage = httpsCallable<
        { name: string; email: string; message: string },
        SubmitContactMessageResponse
      >(functions, "submitContactMessage");
      await submitContactMessage({ name, email, message });
      setStatus("success");
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      setStatus("error");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (status === "success") {
    return (
      <div className="surface-card p-6 text-center">
        <p className="text-foreground/80">{t("contactPage.form.success")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="surface-card space-y-2.5 p-4 sm:space-y-3 sm:p-5">
      <input
        required
        placeholder={t("contactPage.form.namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
      />
      <input
        required
        type="email"
        placeholder={t("contactPage.form.emailPlaceholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
      />
      <textarea
        required
        rows={3}
        placeholder={t("contactPage.form.messagePlaceholder")}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
      />
      {status === "error" && (
        <p className="text-sm text-red-700">{t("contactPage.form.error")}</p>
      )}
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-xl bg-accent px-8 py-2.5 font-semibold text-accent-foreground disabled:opacity-50"
      >
        {loading ? t("contactPage.form.submitLoading") : t("contactPage.form.submitIdle")}
      </button>
    </form>
  );
}
